import { useMemo } from 'react';

import {
  allModalsClosed,
  modalClosed,
  modalOpened,
  selectModalStack,
} from '@/shared/store/reducers/modal/modal.slice';
import { type ModalData, type ModalEntry } from '@/shared/store/reducers/modal/modal.types';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';

export interface UseModal {
  stack: ModalEntry[];
  openModal: (type: string, data?: ModalData) => void;
  /** Closes a named modal, or the top one when called with nothing. */
  closeModal: (type?: string) => void;
  closeAllModals: () => void;
  isModalOpen: (type: string) => boolean;
  modalData: (type: string) => ModalData | undefined;
}

/**
 * The only way a surface opens a dialog (W2/W9).
 *
 * A caller dispatches a TYPE STRING, never a component, which is what lets one
 * module open another module's dialog without importing it — the `app.config.tsx`
 * bridge is the only place that knows what the string means.
 */
export function useModal(): UseModal {
  const dispatch = useAppDispatch();
  const stack = useAppSelector(selectModalStack);

  return useMemo(
    () => ({
      stack,
      openModal: (type: string, data?: ModalData) => {
        dispatch(modalOpened({ type, data }));
      },
      closeModal: (type?: string) => {
        dispatch(modalClosed(type));
      },
      closeAllModals: () => {
        dispatch(allModalsClosed());
      },
      isModalOpen: (type: string) => stack.some((entry) => entry.type === type),
      modalData: (type: string) => stack.find((entry) => entry.type === type)?.data,
    }),
    [dispatch, stack],
  );
}
